import { useState, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import { postalCodesApi } from '../../services/api';
import { MapPin, Sprout } from 'lucide-react';

/**
 * Step1GardenBasics - Garden basics input form (Step 1 of wizard)
 *
 * Matches mockup: docs/design/screenshots/05_Dialog_Garten_Einstellung.png
 *
 * Design features:
 * - Gray rounded boxes around each section
 * - Auto-filled location data displayed as read-only text
 * - Orientation dropdown with 8 directions (N, NO, O, SO, S, SW, W, NW)
 * - Soil type dropdown with 6 options (alphabetically sorted)
 * - pH slider with proper handle styling (white stroke, shadow, green fill)
 * - pH value displayed to the right with color feedback (red/yellow/green/blue)
 * - pH label below value showing acidity level
 * - Tooltips on all inputs for user guidance
 * - Validation errors displayed inline (right of inputs) to prevent layout shift
 */
const Step1GardenBasics = () => {
  const { gardenData, updateBasics, validationErrors } = useWizard();
  const { basics } = gardenData;

  // Local state for auto-filled location data
  const [locationData, setLocationData] = useState({
    city: null,
    country: null,
    hardinessZone: null,
  });

  // Local state for multiple-result dropdown
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState('');

  // Local state for garden name validation
  const [nameError, setNameError] = useState('');

  // Local state for PLZ lookup feedback (no result for the entered ZIP)
  const [locationError, setLocationError] = useState('');

  // Fetch location data from API based on ZIP code (debounced 300ms). Both the
  // lookup and the "no ZIP" reset run inside the timer so there is no synchronous
  // setState in the effect body (and clearing the ZIP is debounced consistently).
  useEffect(() => {
    const zipCode = basics.zipCode;
    const timer = setTimeout(async () => {
      if (!zipCode || zipCode.length < 4) {
        setLocationData({ city: null, country: null, hardinessZone: null });
        setLocationOptions([]);
        setSelectedPlace('');
        setLocationError('');
        updateBasics({
          city: null,
          country: null,
          hardinessZone: null,
          latitude: null,
          longitude: null,
        });
        return;
      }
      try {
        const data = await postalCodesApi.lookup(zipCode);
        if (data.count === 1) {
          const result = data.results[0];
          const zone = result.usda_zone ? `Zone ${result.usda_zone}` : null;
          setLocationData({ city: result.place, country: result.country_name, hardinessZone: zone });
          setLocationOptions([]);
          setSelectedPlace('');
          setLocationError('');
          updateBasics({
            city: result.place,
            country: result.country_name,
            hardinessZone: zone,
            // Koordinaten für den Sonnenstand (#604) — beim eindeutigen Treffer
            // direkt mitschreiben; null, falls die PLZ keine Geo-Daten hat.
            latitude: result.lat ?? null,
            longitude: result.lon ?? null,
          });
        } else if (data.count > 1) {
          setLocationOptions(data.results);
          setSelectedPlace('');
          setLocationError('');
          // City stays empty until user picks from dropdown
        } else {
          setLocationData({ city: null, country: null, hardinessZone: null });
          setLocationOptions([]);
          setSelectedPlace('');
          setLocationError('Keine Daten zu dieser PLZ gefunden.');
          updateBasics({
            city: null,
            country: null,
            hardinessZone: null,
            latitude: null,
            longitude: null,
          });
        }
      } catch (error) {
        console.error('PLZ lookup failed:', error);
        // Network/lookup failure is a different concern than "no result";
        // don't show the "keine Daten" hint here.
        setLocationError('');
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [basics.zipCode, updateBasics]);

  const handlePlaceSelect = (e) => {
    const idx = e.target.value;
    if (idx !== '') {
      const result = locationOptions[parseInt(idx, 10)];
      const zone = result.usda_zone ? `Zone ${result.usda_zone}` : null;
      setLocationData({ city: result.place, country: result.country_name, hardinessZone: zone });
      setLocationOptions([]); // Switch from dropdown back to text display
      setSelectedPlace('');
      updateBasics({
        city: result.place,
        country: result.country_name,
        hardinessZone: zone,
        latitude: result.lat ?? null,
        longitude: result.lon ?? null,
      });
    } else {
      setLocationData({ city: null, country: null, hardinessZone: null });
      updateBasics({
        city: null,
        country: null,
        hardinessZone: null,
        latitude: null,
        longitude: null,
      });
    }
  };


  const handleChange = (field, value) => {
    updateBasics({ [field]: value });
  };

  /**
   * Validate garden name
   * Rules:
   * - Not empty (after trim)
   * - 1-200 characters
   * - Only safe characters (letters, numbers, spaces, basic punctuation, German umlauts)
   */
  const validateGardenName = (name) => {
    const trimmed = name.trim();

    if (!trimmed) {
      return { valid: false, error: 'Bitte geben Sie einen Gartennamen ein.' };
    }

    if (trimmed.length > 200) {
      return { valid: false, error: 'Der Gartenname darf maximal 200 Zeichen lang sein.' };
    }

    // Allow: letters, numbers, spaces, hyphens, underscores, dots, commas, apostrophes, quotes, parentheses
    // Includes German umlauts: ä, ö, ü, ß, Ä, Ö, Ü
    const validPattern = /^[a-zA-ZäöüßÄÖÜ0-9\s\-_.,'"()]+$/;

    if (!validPattern.test(trimmed)) {
      return {
        valid: false,
        error: 'Der Gartenname enthält ungültige Zeichen. Erlaubt sind Buchstaben, Zahlen, Leerzeichen und -_.,\'"()',
      };
    }

    return { valid: true, error: null };
  };

  /**
   * Handle garden name blur event (trim whitespace and validate)
   */
  const handleNameBlur = (e) => {
    const trimmed = e.target.value.trim();

    // Update with trimmed value if different
    if (trimmed !== e.target.value) {
      handleChange('name', trimmed);
    }

    // Validate
    const { error } = validateGardenName(trimmed);
    setNameError(error || '');
  };

  // Helper function to get pH color and label
  const getpHColor = (value) => {
    const pH = value || 7.0;
    if (pH < 5.5) return { color: '#EF4444', label: 'Sauer' }; // Red
    if (pH < 6.5) return { color: '#F59E0B', label: 'Leicht sauer' }; // Yellow/Orange
    if (pH < 7.5) return { color: '#10B981', label: 'Neutral' }; // Green
    return { color: '#3B82F6', label: 'Alkalisch' }; // Blue
  };

  return (
    <div className="space-y-6">
      {/* Garden Name Input - FIRST FIELD */}
      <div>
        <label
          htmlFor="garden-name"
          className="block text-xs font-medium text-gray-600 mb-1"
        >
          Gartenname <span className="text-red-500">*</span>
        </label>

        <input
          type="text"
          id="garden-name"
          value={basics.name || ''}
          onChange={(e) => {
            handleChange('name', e.target.value);
            // Clear error on change
            if (nameError) setNameError('');
          }}
          onBlur={handleNameBlur}
          maxLength={200}
          className={`w-full h-10 px-3 border rounded-lg text-sm transition-colors ${
            nameError
              ? 'border-red-500 bg-red-50'
              : 'border-gray-200 focus:border-blue-500'
          } focus:outline-none focus:ring-2 focus:ring-blue-500`}
          placeholder="z.B. Mein Garten, Vorgarten, Hinterhof..."
          aria-invalid={!!nameError}
          aria-describedby={nameError ? 'name-error' : undefined}
        />

        {/* Character count - only show when approaching limit */}
        {basics.name && basics.name.length > 180 && (
          <p
            className={`text-xs mt-1 ${
              basics.name.length > 195
                ? 'text-yellow-500 font-medium'
                : 'text-gray-500'
            }`}
          >
            {basics.name.length}/200 Zeichen
          </p>
        )}

        {/* Error message */}
        {nameError && (
          <p
            id="name-error"
            className="text-xs text-red-500 mt-1 flex items-center gap-1"
            role="alert"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {nameError}
          </p>
        )}
      </div>

      {/* Section 1: Standort & Klima */}
      <div>
        {/* Section Header - Outside the box */}
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-5 h-5 text-gray-700" />
          <h3 className="text-base font-bold text-gray-900">Standort & Klima</h3>
        </div>

        {/* Gray box container */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
          {/* ZIP Code Input - Horizontal layout with fixed width */}
          <div className="flex items-center">
            <label htmlFor="zipCode" className="text-base font-bold text-gray-900 w-40">
              Postleitzahl
            </label>
            <input
              type="text"
              id="zipCode"
              maxLength="5"
              value={basics.zipCode || ''}
              onChange={(e) => handleChange('zipCode', e.target.value || null)}
              className="w-[200px] px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="50667"
              aria-invalid={!!locationError}
              aria-describedby={locationError ? 'location-error' : undefined}
            />
          </div>

          {/* PLZ lookup feedback — shown when the entered ZIP has no match */}
          {locationError && (
            <p id="location-error" className="text-sm text-red-600 ml-40" role="alert">
              {locationError}
            </p>
          )}

          {/* Auto-filled Location Data - Always visible with labels */}
          <div className="flex items-center">
            <span className="text-base font-bold text-gray-900 w-40">Stadt</span>
            {locationOptions.length > 1 ? (
              <select
                value={selectedPlace}
                onChange={handlePlaceSelect}
                data-testid="place-dropdown"
                className="w-[200px] px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">Bitte Ort wählen...</option>
                {locationOptions.map((opt, i) => (
                  <option key={i} value={i}>{opt.place} ({opt.country_name})</option>
                ))}
              </select>
            ) : (
              <span className="text-sm text-gray-900">{locationData.city || '-'}</span>
            )}
          </div>
          <div className="flex items-center">
            <span className="text-base font-bold text-gray-900 w-40">Land</span>
            <span className="text-sm text-gray-900">{locationData.country || '-'}</span>
          </div>
          <div className="flex items-center">
            <span className="text-base font-bold text-gray-900 w-40">Winterhärte</span>
            <span className="text-sm text-gray-900">{locationData.hardinessZone || '-'}</span>
          </div>

          {/* Orientation Dropdown - 8 directions */}
          <div className="flex items-center">
            <label htmlFor="orientation" className="text-base font-bold text-gray-900 w-40" title="Richtung, in die Ihr Garten ausgerichtet ist">
              Ausrichtung
            </label>
            <select
              id="orientation"
              value={basics.orientation || ''}
              onChange={(e) => handleChange('orientation', e.target.value || null)}
              title="Richtung, in die Ihr Garten ausgerichtet ist"
              className="w-[200px] px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Bitte wählen...</option>
              <option value="north">Nord (N)</option>
              <option value="northeast">Nordost (NO)</option>
              <option value="east">Ost (O)</option>
              <option value="southeast">Südost (SO)</option>
              <option value="south">Süd (S)</option>
              <option value="southwest">Südwest (SW)</option>
              <option value="west">West (W)</option>
              <option value="northwest">Nordwest (NW)</option>
            </select>
          </div>

          {/* Sunlight Dropdown */}
          <div className="flex items-center">
            <label htmlFor="sunlight" className="text-base font-bold text-gray-900 w-40" title="Sonneneinstrahlung in Ihrem Garten">
              Sonnenlicht
            </label>
            <select
              id="sunlight"
              value={basics.sunlight || ''}
              onChange={(e) => handleChange('sunlight', e.target.value || null)}
              title="Sonneneinstrahlung in Ihrem Garten"
              className="w-[200px] px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="">Bitte wählen...</option>
              <option value="full_sun">Vollsonnig (6+ Std.)</option>
              <option value="sun-partial_shade">Sonnig-Halbschattig (4-6 Std.)</option>
              <option value="partial_shade">Halbschattig (2-4 Std.)</option>
              <option value="full_shade">Schattig (&lt; 2 Std.)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Section 2: Bodenverhältnisse */}
      <div>
        {/* Section Header - Outside the box */}
        <div className="flex items-center gap-2 mb-3">
          <Sprout className="w-5 h-5 text-gray-700" />
          <h3 className="text-base font-bold text-gray-900">Bodenverhältnisse</h3>
        </div>

        {/* Gray box container */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
          {/* Soil Type Dropdown - Horizontal layout */}
          <div className="flex items-center">
            <label htmlFor="soilType" className="text-base font-bold text-gray-900 w-40">
              Bodenart
            </label>
            <select
              id="soilType"
              value={basics.soilType || ''}
              onChange={(e) => handleChange('soilType', e.target.value || null)}
              title="Bodenart in Ihrem Garten"
              className={`w-[200px] px-3 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 ${
                validationErrors.soilType && !basics.soilType ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Bitte wählen...</option>
              <option value="humus">Humusreich</option>
              <option value="loamy">Lehmig</option>
              <option value="loess">Lössboden</option>
              <option value="mixed">Mischboden</option>
              <option value="sandy">Sandig</option>
              <option value="clay">Tonig</option>
            </select>
          </div>
          {validationErrors.soilType && !basics.soilType && (
            <p className="text-sm text-red-600 ml-40">{validationErrors.soilType}</p>
          )}

          {/* pH Value Slider - Slider below label with value on right */}
          <div>
            <label htmlFor="phValue" className="block text-base font-bold text-gray-900 mb-2" title="pH-Wert des Bodens">
              pH
            </label>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                {/* Custom styled slider */}
                <div className="relative">
                  <input
                    type="range"
                    id="phValue"
                    min="4.0"
                    max="8.0"
                    step="0.1"
                    value={basics.phValue || 7.0}
                    onChange={(e) => handleChange('phValue', parseFloat(e.target.value))}
                    title="pH-Wert des Bodens"
                    className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer slider-emerald"
                    style={{
                      background: `linear-gradient(to right, #10b981 0%, #10b981 ${
                        ((basics.phValue - 4) / 4) * 100
                      }%, #e5e7eb ${((basics.phValue - 4) / 4) * 100}%, #e5e7eb 100%)`,
                    }}
                  />
                </div>
                {/* pH scale labels */}
                <div className="flex justify-between text-xs text-gray-500 mt-1 px-0.5">
                  <span>4</span>
                  <span>5</span>
                  <span>6</span>
                  <span>7</span>
                  <span>8</span>
                </div>
              </div>
              {/* pH value displayed to the right of slider with color feedback */}
              <div className="w-16 text-right">
                <span
                  className="text-sm font-semibold block"
                  style={{ color: getpHColor(basics.phValue).color }}
                >
                  {basics.phValue?.toFixed(1) || '7.0'}
                </span>
                <span
                  className="text-xs block"
                  style={{ color: getpHColor(basics.phValue).color }}
                >
                  {getpHColor(basics.phValue).label}
                </span>
              </div>
            </div>
          </div>

          {/* Drainage Radio Buttons - Below label, evenly spaced */}
          <div>
            <span className="block text-base font-bold text-gray-900 mb-2" title="Wie schnell versickert Wasser nach Regen?">
              Drainage
            </span>
            <div className="flex justify-evenly">
              {[
                { value: 'good', label: 'gut' },
                { value: 'medium', label: 'mittel' },
                { value: 'poor', label: 'schlecht' },
              ].map(({ value, label }) => (
                <label key={value} className="flex flex-col items-center gap-1 cursor-pointer" title="Wie schnell versickert Wasser nach Regen?">
                  <input
                    type="radio"
                    name="drainage"
                    value={value}
                    checked={basics.drainage === value}
                    onChange={(e) => handleChange('drainage', e.target.value)}
                    className="sr-only peer"
                  />
                  {/* Radio button - 20px, fully filled emerald-500 when active, gray-100 when inactive, 2px black border */}
                  <div
                    className={`w-5 h-5 rounded-full border-2 border-black ${
                      basics.drainage === value ? 'bg-emerald-500' : 'bg-gray-100'
                    }`}
                  />
                  <span className="text-sm font-normal text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for slider handle */}
      <style>{`
        .slider-emerald::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #10b981;
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          cursor: pointer;
        }

        .slider-emerald::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #10b981;
          border: 3px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default Step1GardenBasics;
